/**
 * MemoryPolicyPanel
 * Compact, read-only view of the agent's memory policy. Intended to sit
 * above the memory browser as the new unified "Memory" tab.
 */

import React from 'react';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import {
  Edit as EditIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';

import type { AgentDetail as AgentDetailType } from '../../../types';
import { SectionCard } from './primitives';

interface MemoryPolicyPanelProps {
  agent: AgentDetailType;
  onEdit: () => void;
}

const PolicyRow: React.FC<{
  label: string;
  value: React.ReactNode;
  helper?: string;
}> = ({ label, value, helper }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.65rem' }}>
      {label}
    </Typography>
    <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
      {value}
    </Typography>
    {helper ? (
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    ) : null}
  </Box>
);

export const MemoryPolicyPanel: React.FC<MemoryPolicyPanelProps> = ({ agent, onEdit }) => {
  const c = agent.config || {};
  const m = agent.memory_config || {};
  const semanticConfig = m.semantic_search || {};
  const memoryEnabled = m.enabled !== false;
  const contextMemories = m.max_context_memories ?? 5;
  const perTypeCap = m.max_memories_per_type ?? 500;
  const autoSummarize = c.auto_summarize !== false;
  const summarizationStrategy = c.summarization_strategy ?? 'context_window';
  const contextThreshold = c.summarization_context_usage_threshold ?? 0.75;
  const semantic = Boolean(
    semanticConfig.enabled ?? m.enable_semantic_search ?? false
  );
  const toolMemory = c.tool_memory_enabled !== false;

  return (
    <SectionCard
      icon={<TuneIcon fontSize="small" />}
      title="Memory policy"
      description="Controls how this agent stores, prunes, and retrieves memories."
      actions={
        <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
          Edit policy
        </Button>
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PolicyRow
            label="Durable memory"
            value={memoryEnabled ? 'On' : 'Off'}
            helper="Prompt context"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PolicyRow
            label="Context memories"
            value={contextMemories.toLocaleString()}
            helper="Per execution"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PolicyRow
            label="Per-type cap"
            value={perTypeCap.toLocaleString()}
            helper="By memory type"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PolicyRow
            label="Max iterations"
            value={c.max_iterations ?? 100}
            helper="Tool loop"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PolicyRow
            label="Delegation timeout"
            value={`${c.delegation_timeout_seconds ?? 900}s`}
            helper="AGENT expert wait"
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={0.75} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={`Memory: ${memoryEnabled ? 'On' : 'Off'}`}
          color={memoryEnabled ? 'success' : 'default'}
          variant={memoryEnabled ? 'filled' : 'outlined'}
        />
        <Chip
          size="small"
          label={`Auto-summarise: ${autoSummarize ? 'On' : 'Off'}`}
          color={autoSummarize ? 'success' : 'default'}
          variant={autoSummarize ? 'filled' : 'outlined'}
        />
        <Chip
          size="small"
          label={`Tool memory: ${toolMemory ? 'On' : 'Off'}`}
          color={toolMemory ? 'success' : 'default'}
          variant={toolMemory ? 'filled' : 'outlined'}
        />
        <Chip
          size="small"
          label={`Semantic search: ${semantic ? 'On' : 'Off'}`}
          color={semantic ? 'success' : 'default'}
          variant={semantic ? 'filled' : 'outlined'}
        />
        {autoSummarize ? (
          <Chip
            size="small"
            variant="outlined"
            label={
              summarizationStrategy === 'context_window'
                ? `Trigger @ ${Math.round(contextThreshold * 100)}% context`
                : `Trigger @ ${c.max_context_messages ?? 20} msgs`
            }
          />
        ) : null}
        <Chip
          size="small"
          variant="outlined"
          label={`Temp ${c.temperature ?? 0.7}`}
        />
      </Stack>
    </SectionCard>
  );
};

export default MemoryPolicyPanel;
